import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SharedModule } from '@/app/shared/shared.module';
import { TranslateService } from '@ngx-translate/core';
import {
  FileParameter,
  FileProxy,
  FileUploadResultViewModelResponseResult,
  GuidResponseResult,
  KeyValueModel,
  NoteFileView,
  NoteProxy,
  NoteTagsLookupModel,
  NoteViewModel,
  PolygonItem,
  SatelliteProblematicSpotView,
} from '@/app/proxy/proxies';
import { MapBoxService } from '@/app/core/services/mapbox.service';
import { PlantationService } from '@/app/core/services/plantation.service';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { finalize, Observable } from 'rxjs';
import { base64ToBlob } from '@/app/core/helpers/base-to-blob.helper';
import { point, polygon } from '@turf/helpers';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { ToastService } from '@/app/core/services/toast.service';
import { CropHealthService } from '../../../../services/crop-health.service';
import { MapMessageTypeEnum } from '../../crop-health-map/crop-health-map-message-overlay/crop-health-map-message-overlay.component';
import { MixpanelTrackerService } from '@/app/core/analytics';

export enum NoteTagEnum {
  Irrigation = '84c49042-dfd9-43a8-8049-85f4aabf05a2',
  Pest = '6614b04c-82b4-4788-a41b-1258814421a5',
  Disease = 'aa5a16a9-89b3-41aa-a1ad-5765fbf64897',
  Nutrition = '285b6e12-ad96-4d45-ac0c-50d394fdc2fe',
  Weed = '721b16fe-9022-4b2c-8f1c-01d1b1bea807',
  Other = '0a08b839-41ca-4884-90c1-86e55e8a0314',
}

export enum CursorEnums {
  DEFAULT = 'default',
  POINTER = 'pointer',
  PIN = "url('assets/images/pin-pointer-cursor.svg'), auto",
}

@Component({
  selector: 'app-crop-health-note-form',
  imports: [SharedModule],
  templateUrl: './crop-health-note-form.component.html',
  styleUrl: './crop-health-note-form.component.less',
  providers: [NoteProxy, FileProxy, MapBoxService],
})
export class CropHealthNoteFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() note: NoteViewModel;
  @Input() editMode: boolean;
  @Input() mapInstance: mapboxgl.Map;
  @Input() problematicSpots: SatelliteProblematicSpotView[] = [];

  @Output() readonly back = new EventEmitter();
  @Output() readonly addMarker = new EventEmitter();
  @Output() readonly removeMarker = new EventEmitter();

  private hadOutsidePlantationAreaError = false;

  noteTags: NoteTagsLookupModel[] = [];
  noteTypes: KeyValueModel[] = [];
  problematicSpotStatuses: KeyValueModel[] = [];

  selectedNoteTag: string;

  form: FormGroup;
  fileUploadLoading = false;

  noteFiles: NoteFileView[] = [];
  uploadedFileIds: string[] = [];

  disableAll = true;
  disableForm = false;

  selectedProblematicSpotStatus: 'problemDetected' | 'noProblem' = 'problemDetected';
  currentProblematicSpotIndex = 0;

  submitLoading = false;

  constructor(
    private fb: FormBuilder,
    private noteProxy: NoteProxy,
    private fileProxy: FileProxy,
    private mapBoxService: MapBoxService,
    private plantationSelectPanelService: PlantationService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private mixpanelTracker: MixpanelTrackerService,
    private cropHealthService: CropHealthService,
    @Optional() private dialogRef: DynamicDialogRef
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mapInstance'] && this.mapInstance) {
      if (!this.problematicSpots || this.problematicSpots?.length === 0) {
        this.mapInstance!.on('click', (e: any) => {
          this.onAddPinToMap(e);
        });
      }
    }

    if (changes['note'] && this.note) {
      this.disableAll = false;
      this.setForm();
      if (this.note.noteTags?.length > 0) {
        this.selectedNoteTag = this.note.noteTags[0].id;
      }

      if (this.note.fileList?.length) {
        this.noteFiles = [...this.note.fileList];
        for (const item of this.note.fileList) {
          this.uploadedFileIds.push(item.id);
        }
      }

      this.selectedNoteTag = this.note.noteTags?.length > 0 ? this.note.noteTags[0].id : null;
    }

    if (changes['problematicSpots'] && this.problematicSpots?.length > 0) {
      this.disableAll = false;
      this.markCurrentSpotOnTheMap();
    }
  }

  ngOnInit(): void {
    this.setForm();

    this.cropHealthService.noteLookups$.subscribe(res => {
      this.noteTags = res.noteTags ?? [];
      this.noteTypes = res.noteTypes ?? [];
      this.problematicSpotStatuses = res.problematicSpotStatuses ?? [];
    })

    if (!(this.problematicSpots?.length > 0)) {
      this.form.get('noteTitle').setValidators(Validators.required);
    }
  }

  setForm() {
    this.form = this.fb.group({
      noteTitle: [
        {
          value: this.note?.title,
          disabled: this.disableAll,
        },
        [Validators.maxLength(20)],
      ],
      noteContent: [
        {
          value: this.note?.noteContent,
          disabled: this.disableAll,
        },
        [Validators.maxLength(1000)],
      ],
      latitude: [this.note?.coordinate?.latitude],
      longitude: [this.note?.coordinate?.longitude],
    });

    if (!(this.problematicSpots?.length > 0) && !this.note?.problematicSpotId) {
      this.form.controls['latitude'].setValidators(Validators.required);
      this.form.controls['longitude'].setValidators(Validators.required);
    }
  }

  saveNote() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (!(this.problematicSpots?.length > 0) && !this.selectedNoteTag) {
      this.toastService.warn({ detail: 'doktar_app.services.crop_monitoring.select_a_note_tag_warning' });
      return;
    }

    const formData = this.form.value;

    const payload: any = {
      noteTypeId: '6d15b904-bbca-43a6-b08c-c21c05dbfa56', // 'Plantation Observation',
      noteTagIds: this.selectedNoteTag ? [this.selectedNoteTag] : [],
      plantationId: this.plantationSelectPanelService.selectedPlantation.id,
      landId: this.plantationSelectPanelService.selectedLand.id,
      problematicSpotId: null,
      problematicSpotStatusId: null,
      coordinate: {
        latitude: formData.latitude,
        longitude: formData.longitude,
      } as PolygonItem,
      title: formData.noteTitle,
      noteContent: formData.noteContent,
      fileIdList: [...this.uploadedFileIds],
    };

    let request: Observable<GuidResponseResult>;
    if (this.note) {
      payload['id'] = this.note.id;
      request = this.noteProxy.note_UpdateNote(payload);
    } else {
      request = this.noteProxy.note_CreateNote(payload);
    }

    this.submitLoading = true;
    request
      .pipe(
        finalize(() => {
          this.submitLoading = false;
        })
      )
      .subscribe((res) => {
        if (res?.isSuccess) {
          if (!this.note) {
            this.trackServicesNoteAddingCompleted();
          } else {
            this.trackServicesNoteEdited();
          }
          const detail = this.note
            ? 'doktar_app.services.crop_monitoring.note_updated_message'
            : 'doktar_app.services.crop_monitoring.note_add_success';
          this.toastService.success({ detail });

          setTimeout(() => {
            this.reset();
            if (this.dialogRef) {
              this.dialogRef.close(true);
            } else {
              this.back.emit(true);
            }
          }, 100);
        } else {
          this.toastService.error({ detail: res.errors[0]?.message });
        }
      });
  }

  saveProblematicSpotNote() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formData = this.form.value;
    let problematicSpotStatusId: string;
    let problematicSpotId: string;
    if (!this.note) {
      problematicSpotId = this.problematicSpots[this.currentProblematicSpotIndex].problematicSpotId;
      if (this.selectedProblematicSpotStatus === 'noProblem') {
        // Checked – No Problematic Spot Found
        problematicSpotStatusId = '1e9879ee-28fe-499f-8657-f8577780359a';
      } else {
        // Checked – Problematic Spot Found
        problematicSpotStatusId = '9b44ad1b-3614-450d-a7db-ab74a6ba9b13';
      }
    } else {
      problematicSpotStatusId = this.problematicSpotStatuses.find(
        (e) => e.value === this.note.problematisSpotStatus
      )?.key;
      problematicSpotId = this.note.problematicSpotId;
    }

    const payload: any = {
      noteTypeId: 'e0855215-44a1-40d7-86b0-c1caa0fe6ef1', // Problematic Spot
      noteTagIds: this.selectedNoteTag ? [this.selectedNoteTag] : [],
      plantationId: this.plantationSelectPanelService.selectedPlantation.id,
      landId: this.plantationSelectPanelService.selectedLand.id,
      problematicSpotId,
      problematicSpotStatusId,
      coordinate: null,
      title: formData.noteTitle,
      noteContent: formData.noteContent,
      fileIdList: [...this.uploadedFileIds],
    };

    let request: Observable<GuidResponseResult>;
    if (this.note) {
      payload['id'] = this.note.id;
      request = this.noteProxy.note_UpdateNote(payload);
    } else {
      request = this.noteProxy.note_CreateNote(payload);
    }

    this.submitLoading = true;
    request
      .pipe(
        finalize(() => {
          this.submitLoading = false;
        })
      )
      .subscribe((res) => {
        if (res?.isSuccess) {
          this.trackServicesProblemSpotChecked(false);
          const detail = res.result
            ? 'doktar_app.services.crop_monitoring.note_added_message'
            : 'doktar_app.common.action_performed_successfully';
          this.toastService.success({ detail });

          // reset note form
          this.form.reset();
          this.updateProblemSpot('problemDetected');
          this.noteFiles = [];
          this.uploadedFileIds = [];
          this.selectedNoteTag = null;

          if (this.currentProblematicSpotIndex < this.problematicSpots.length - 1) {
            this.currentProblematicSpotIndex += 1;
            this.markCurrentSpotOnTheMap();
          } else {
            setTimeout(() => {
              this.reset();
              if (this.dialogRef) {
                this.dialogRef.close(true);
              } else {
                this.back.emit(true);
              }
            }, 100);
          }
        } else {
          this.toastService.error({ detail: res.errors[0]?.message });
        }
      });
  }

  updateProblemSpot(status: 'problemDetected' | 'noProblem') {
    this.selectedProblematicSpotStatus = status;
    if (status === 'problemDetected') {
      this.disableForm = false;
      this.form.enable();
    } else {
      this.disableForm = true;
      this.form.reset();
      this.selectedNoteTag = null;
      this.form.disable();
    }
  }

  onSelectFile(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      if (!file.type.startsWith('image/')) {
        console.warn('Selected file is not an image.');
        return;
      }

      try {
        const reader = new FileReader();
        reader.onload = () => {
          const imageContent = reader.result as string;

          const blob = base64ToBlob(imageContent, file.type);

          const fileParameter: FileParameter = {
            data: blob,
            fileName: file.name,
          };
          const fileTypeId = 'D0C96095-A377-44DD-8B23-AEB000DEB180';
          this.fileUploadLoading = true;
          this.fileProxy
            .file_Upload(fileTypeId, fileParameter)
            .pipe(
              finalize(() => {
                this.fileUploadLoading = false;
              })
            )
            .subscribe({
              next: (res: FileUploadResultViewModelResponseResult) => {
                if (res.isSuccess) {
                  this.uploadedFileIds.push(res.result.id);
                  this.noteFiles.push({
                    id: res.result.id,
                    photoUrl: imageContent,
                  } as NoteFileView);
                } else {
                  this.toastService.error({ detail: res.errors[0]?.message });
                }
              },
            });
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error(error)
      }
    }
  }

  onAddPinToMap(e: any) {
    this.showWrongPinMessage(false);
    const boundaryPolygon = polygon([this.plantationSelectPanelService.selectedPlantation.boundaries]);

    const clickedPoint = point([e.lngLat.lng, e.lngLat.lat]);
    const isInside = booleanPointInPolygon(clickedPoint, boundaryPolygon);

    if (isInside) {
      this.form.get('noteTitle')?.enable();
      this.form.get('noteContent')?.enable();
      this.disableAll = false;
      this.form.get('longitude').setValue(e.lngLat.lng);
      this.form.get('latitude').setValue(e.lngLat.lat);
      this.addMarker.emit(e.lngLat);
    } else {
      this.showWrongPinMessage(true);
    }
  }

  showWrongPinMessage(event: boolean) {
    if (event) {
      this.hadOutsidePlantationAreaError = true;
      this.cropHealthService.setMapOverlayMessage(MapMessageTypeEnum.outsidePlantationArea)
    } else {
      this.cropHealthService.setMapOverlayMessage(undefined)
    }
  }

  removeImgFile(file: NoteFileView) {
    if (!this.fileUploadLoading) {
      this.noteFiles = this.noteFiles.filter((img) => img.id !== file.id);
      this.uploadedFileIds = this.uploadedFileIds.filter((id) => id !== file.id);
    }
  }

  markCurrentSpotOnTheMap() {
    const boundaries = [];
    for (const item of this.problematicSpots[this.currentProblematicSpotIndex].boundaries) {
      boundaries.push([item.longitude, item.latitude]);
    }
    this.mapBoxService.addDrawablePolygon(boundaries, this.mapInstance);
  }

  onBackBtn() {
    this.reset();
    this.back.emit();
    this.removeMarker.emit();

    // enable zoom
    this.mapInstance.scrollZoom.enable();
    this.mapInstance.doubleClickZoom.enable();
    this.mapInstance.touchZoomRotate.enable(); // enables both zoom and rotate on touch
    this.mapInstance.boxZoom.disable(); // disables zoom using shift + drag
  }

  reset() {
    this.form.reset();
    this.noteFiles = [];
    this.uploadedFileIds = [];
    this.selectedNoteTag = null;
    this.problematicSpots = [];
    this.hadOutsidePlantationAreaError = false;
    this.mapBoxService.deleteDrawing(this.mapInstance);
    this.removeMarker.emit();
  }

  next(skip = false) {
    if (!skip) {
      this.saveProblematicSpotNote();
    } else {
      this.trackServicesProblemSpotChecked(true);
      if (this.currentProblematicSpotIndex < this.problematicSpots.length - 1) {
        this.currentProblematicSpotIndex += 1;

        this.markCurrentSpotOnTheMap();
      }
    }
  }

  private trackServicesProblemSpotChecked(skipped: boolean): void {
    const plantation = this.plantationSelectPanelService.selectedPlantation;
    if (!plantation) {
      return;
    }

    const land = this.plantationSelectPanelService.selectedLand;
    const enterpriseId = land?.enterpriseId || this.plantationSelectPanelService.enterpriseId;
    const enterprise = this.plantationSelectPanelService.originalResult?.find((item) => item.id === enterpriseId);
    const noteTitle = this.form.get('noteTitle')?.value?.trim() ?? '';
    const noteContent = this.form.get('noteContent')?.value?.trim() ?? '';
    const problemType = this.noteTags.find((item) => item.key === this.selectedNoteTag)?.value;

    this.mixpanelTracker.trackServicesProblemSpotChecked({
      'problem detected': this.selectedProblematicSpotStatus === 'problemDetected',
      'note title': noteTitle || undefined,
      'problem type': problemType,
      'description entered': !!noteContent,
      'number of photo': this.noteFiles.length,
      skipped,
      'plantation id': plantation.id,
      'plantation name': plantation.name,
      'land id': land?.id,
      'land name': land?.name,
      'enterprise id': enterpriseId,
      'enterprise name': enterprise?.enterpriseName,
    });
  }

  private trackServicesNoteAddingCompleted(): void {
    const plantation = this.plantationSelectPanelService.selectedPlantation;
    if (!plantation) {
      return;
    }

    const land = this.plantationSelectPanelService.selectedLand;
    const enterpriseId = land?.enterpriseId || this.plantationSelectPanelService.enterpriseId;
    const enterprise = this.plantationSelectPanelService.originalResult?.find((item) => item.id === enterpriseId);
    const noteTitle = this.form.get('noteTitle')?.value?.trim() ?? '';
    const noteContent = this.form.get('noteContent')?.value?.trim() ?? '';
    const problemType = this.noteTags.find((item) => item.key === this.selectedNoteTag)?.value;

    this.mixpanelTracker.trackServicesNoteAddingCompleted({
      'note title': noteTitle || undefined,
      'problem type': problemType,
      'description entered': !!noteContent,
      'number of photo': this.noteFiles.length,
      'plantation id': plantation.id,
      'plantation name': plantation.name,
      'land id': land?.id,
      'land name': land?.name,
      'enterprise id': enterpriseId,
      'enterprise name': enterprise?.enterpriseName,
      'has premium plan': !!plantation.isPremium,
      'error - outside plantation area': this.hadOutsidePlantationAreaError,
    });

    // apply to the current add-note flow only
    this.hadOutsidePlantationAreaError = false;
  }

  private trackServicesNoteEdited(): void {
    const plantation = this.plantationSelectPanelService.selectedPlantation;
    if (!plantation || !this.note) {
      return;
    }

    const land = this.plantationSelectPanelService.selectedLand;
    const enterpriseId = land?.enterpriseId || this.plantationSelectPanelService.enterpriseId;
    const enterprise = this.plantationSelectPanelService.originalResult?.find((item) => item.id === enterpriseId);
    const oldProblemType = this.note.noteTags?.map((item) => item.name).filter(Boolean).join(', ');
    const newProblemType = this.noteTags.find((item) => item.key === this.selectedNoteTag)?.value;
    const newTitle = this.form.get('noteTitle')?.value?.trim() ?? '';
    const newDescription = this.form.get('noteContent')?.value?.trim() ?? '';
    const oldDescription = this.note.noteContent?.trim() ?? '';

    this.mixpanelTracker.trackServicesNoteEdited({
      'old note title': this.note.title?.trim() || undefined,
      'new note title': newTitle || undefined,
      'old problem type': oldProblemType || undefined,
      'new problem type': newProblemType,
      'old description': oldDescription || undefined,
      'new description': newDescription || undefined,
      'old number of photo': this.note.fileList?.length ?? 0,
      'new number of photo': this.noteFiles.length,
      'plantation id': plantation.id,
      'plantation name': plantation.name,
      'land id': land?.id,
      'land name': land?.name,
      'enterprise id': enterpriseId,
      'enterprise name': enterprise?.enterpriseName,
      'has premium plan': !!plantation.isPremium,
    });
  }

  cancel() {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      this.onBackBtn();
    }
  }

  get notTitleLength() {
    const value = this.form.get('noteTitle')?.value?.trim() ?? '';
    return value.length;
  }

  get notcontentLength() {
    const value = this.form.get('noteContent')?.value?.trim() ?? '';
    return value.length;
  }

  ngOnDestroy(): void {
    this.removeMarker.emit();
  }
}
